'use client'

import React, { useState } from 'react'
import { Mail, CheckCircle, PawPrint } from 'lucide-react'
import { apiSubscribeNewsletter } from '@/apis/page_optimization'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { apiVerifyCaptcha } from '@/apis/captcha'
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider'
import { toast } from 'react-toastify'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', YELLOW: '#FF9934', BROWN: '#8C5826', DARK_BROWN: '#5c3717', LIGHT: '#E1F1F2' }

interface Props { companyId: string; domain?: string }

const PetCareNewsletterInner = ({ companyId, domain }: Props) => {
    const [email, setEmail] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)
    const { executeRecaptcha } = useGoogleReCaptcha()

    const validateEmail = (emailStr: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return regex.test(emailStr)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!email.trim()) {
            toast.error('Email field is required.', { autoClose: 2000 })
            return
        }

        if (!validateEmail(email)) {
            toast.error('Please enter a valid email address.', { autoClose: 2000 })
            return
        }

        setLoading(true)

        try {
            // Step 1: Verify token using domain if recaptcha is available
            if (executeRecaptcha && domain) {
                try {
                    const recaptchaToken = await executeRecaptcha('newsletter_form')
                    if (recaptchaToken) {
                        const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken)
                        if (captchaRes && captchaRes.verified === false) {
                            toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 })
                            setLoading(false)
                            return
                        }
                    }
                } catch (recaptchaErr) {
                    console.warn('reCAPTCHA skipped or failed:', recaptchaErr)
                }
            }

            // Step 2: Subscribe newsletter using companyId
            const response = await apiSubscribeNewsletter(companyId, email)
            const resMsg = typeof response?.message === 'string' ? response.message.trim() : ''
            const resStatus: any = response?.status

            const isSuccess =
                (
                    resStatus === true ||
                    resStatus === 'true' ||
                    resStatus === 200 ||
                    resStatus === '200' ||
                    resStatus === 201 ||
                    resStatus === '201' ||
                    (response as any)?.success === true ||
                    resMsg.toLowerCase().includes('subscribed successfully') ||
                    resMsg.toLowerCase().includes('subscriber successfully added') ||
                    resMsg.toLowerCase().includes('thank you') ||
                    resMsg.toLowerCase().includes('success') ||
                    resMsg.toLowerCase().includes('already') ||
                    resMsg.toLowerCase().includes('exists') ||
                    resMsg.toLowerCase().includes('taken')
                ) &&
                !resMsg.includes('Server responded with status') &&
                !resMsg.toLowerCase().includes('fail') &&
                !resMsg.toLowerCase().includes('not found')

            if (isSuccess) {
                toast.success('Thank you for subscribing!', { autoClose: 2500 })
                setEmail('')
                setSuccess(true)
                setTimeout(() => setSuccess(false), 3000)
            } else if (resMsg && resMsg !== '{}' && !resMsg.includes('Server responded with status')) {
                toast.error(resMsg, { autoClose: 2500 })
            } else {
                toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
            }
        } catch (error) {
            console.error('Error subscribing:', error)
            toast.error('Failed to subscribe. Please try again later.', { autoClose: 2500 })
        } finally {
            setLoading(false)
        }
    }

    return (
        <section className="relative overflow-hidden py-16" style={{ background: `linear-gradient(135deg, ${C.PRIMARY} 0%, ${C.YELLOW} 100%)`, fontFamily: "'Inter', sans-serif" }}>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-30">
                <PawPrint className="text-[18vw]" style={{ color: C.BROWN }} strokeWidth={0.5} />
            </div>
            <div className="relative max-w-4xl mx-auto px-6">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4" style={{ background: C.BROWN }}>
                        <PawPrint size={12} className="text-white" />
                        <span className="text-[10px] font-bold uppercase tracking-wider text-white">Free Deals Weekly</span>
                    </div>
                    <h2 className="font-black text-3xl md:text-4xl mb-3" style={{ color: C.BROWN }}>
                        Get Exclusive <span className="text-white">Pet Deals!</span>
                    </h2>
                    <p className="text-sm" style={{ color: `${C.BROWN}cc` }}>
                        Subscribe to get the latest coupons and discounts for your furry friends.
                    </p>
                </div>

                {success ? (
                    <div className="max-w-md mx-auto text-center p-6 rounded-2xl shadow-md" style={{ background: 'white' }}>
                        <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: C.SECONDARY }}>
                            <CheckCircle size={24} className="text-white" />
                        </div>
                        <p className="font-bold" style={{ color: C.BROWN }}>Thanks for subscribing!</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="max-w-lg mx-auto">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-1">
                                <div className="absolute left-4 top-1/2 -translate-y-1/2">
                                    <Mail size={18} style={{ color: C.BROWN }} />
                                </div>
                                <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    className="w-full pl-11 pr-4 py-3.5 rounded-xl text-base focus:outline-none focus:ring-2 shadow-inner"
                                    style={{ background: 'white', border: '2px solid white', color: C.BROWN }}
                                    required disabled={loading} />
                            </div>
                            <button type="submit" disabled={loading || !email}
                                className="px-8 py-3.5 rounded-xl font-bold transition-all hover:scale-105 disabled:opacity-50 shadow-lg"
                                style={{ background: `linear-gradient(135deg, ${C.BROWN} 0%, ${C.DARK_BROWN} 100%)`, color: 'white' }}>
                                {loading ? 'Joining...' : 'Subscribe'}
                            </button>
                        </div>
                        <p className="text-xs mt-3 text-center" style={{ color: `${C.BROWN}88` }}>
                            No spam, unsubscribe anytime.
                        </p>
                    </form>
                )}
            </div>
        </section>
    )
}

const PetCareNewsletter = (props: Props) => (
    <RecaptchaProvider domain={props.domain || ''}>
        <PetCareNewsletterInner {...props} />
    </RecaptchaProvider>
)

export default PetCareNewsletter